import { Document } from "./Document";
import { OrderForm } from "./OrderFormTypes";
import { Tactic } from "./TacticTypes";
import { User } from "./UserGroup";

// File types that can be uploaded

const systemFiles = ["Thumbs.db", "desktop.ini", "Icon\r"];

/**
 * Whether the file is a hidden or system file.
 */
export const isHiddenOrSystemFile = (fileName: string): boolean => {
    if (fileName.startsWith(".")) return true;
    return systemFiles.includes(fileName);
};

export const allowedExtensions = [
    ".pdf",
    ".doc",
    ".docx",
    ".xls",
    ".xlsx",
    ".ppt",
    ".pptx",
    ".txt",
    ".rtf",
    ".md",
    ".csv",
    ".odt",
    ".ods",
    ".odp",
];

// Whether a filename's extension is allowed to be uploaded.
export const hasAllowedExtension = (fileName: string): boolean => {
    const fileExt = fileName.substring(fileName.lastIndexOf(".")).toLowerCase();
    return allowedExtensions.includes(fileExt);
};

// Project

export enum ProjectType {
    DEAL = "deal",
    RFX = "rfx",
}

export enum ProjectStatus {
    DRAFT = "draft",
    ACTIVE = "active",
    COMPLETED = "completed",
}

export interface ProjectCollaborator {
    user: User;
    canEdit: boolean;
}

export enum ProjectAction {
    VIEW = "view",
    EDIT = "edit",
    DELETE = "delete",
    REASSIGN = "reassign",
    SHARE = "share",
}

export interface ProjectDocument {
    id: number;
    document: Document;
    isDeleted: boolean;
    recordCreationDate: Date;
    creator: User;
}

/** Closing outcome for deal projects. */
export enum DealClosingOutcome {
    SIGNED = "signed",
    CANCELLED = "cancelled",
}

/** Closing outcome for RFX projects. */
export enum RfxClosingOutcome {
    COMPLETED = "completed",
}

/** Outcome of a completed project's closing analysis. */
export type ClosingOutcome = DealClosingOutcome | RfxClosingOutcome;

/** Map from ProjectType to valid closing outcomes. */
export const CLOSING_OUTCOMES_BY_PROJECT_TYPE: Record<ProjectType, readonly ClosingOutcome[]> = {
    [ProjectType.DEAL]: Object.values(DealClosingOutcome),
    [ProjectType.RFX]: Object.values(RfxClosingOutcome),
};

/** "COMPLETE" is a dummy type indicating the project is closed with no actual analysis output — render the input instead. */
export enum AnalysisType {
    DEAL = "deal_analysis",
    RFX = "rfx_analysis",
    ROI = "roi_analysis",
    COMPLETE = "complete",
}

export interface AnalysisSummary {
    type: AnalysisType;
    version: number;
    status: ProjectAnalysisStatus;
}

/**
 * Reference to the origin of a new project, supplied by the client on create.
 * Carries only what the server needs to identify and validate the link;
 * the rich `ProjectSource` returned on responses is built server-side.
 */
export enum ProjectSourceType {
    RFX = "rfx",
}

export interface RfxProjectSourceInput {
    type: ProjectSourceType.RFX;
    rfxVendorId: number;  // the ILRfxVendor row id
}

export type ProjectSourceInput = RfxProjectSourceInput;

/**
 * Origin of a project that was spawned from another, as returned by the
 * server on Project / ProjectSummary responses. Includes denormalized data
 * the UI needs to render the link without an extra round trip.
 */
export interface RfxProjectSource {
    type: ProjectSourceType.RFX;
    projectId: number;    // the parent RFx project id
    projectName: string;  // the parent RFx project name
    rfxVendorId: number;  // the ILRfxVendor row id
}

export type ProjectSource = RfxProjectSource;

export enum RfxVendorStatus {
    CANDIDATE = "candidate",
    SELECTED = "selected",
}

/** One evaluated vendor of an RFx, with selection state and optional spawned deal. */
export interface RfxVendor {
    id: number; // ILRfxVendor row id — use as `source.rfxVendorId` when spawning a deal
    vendorKey: string;
    vendorName: string;
    vendorHero?: string;
    status: RfxVendorStatus;
    dealProjectId?: number;
    dealProject?: ProjectSummary;
}

/** Lightweight project summary for list views. Pre-computes derived values to avoid loading full relation graphs. */
export interface ProjectSummary {
    id: number;
    name: string;
    type: ProjectType;
    status: ProjectStatus;
    latestAnalysis?: AnalysisSummary;
    /** Set only when status is COMPLETED and the latest analysis is a closing analysis. */
    closingOutcome?: ClosingOutcome;
    creatorId: number;
    creatorName: string;
    ownerId: number;
    ownerName?: string;
    recordCreationDate: Date;
    lastUpdateDate: Date;
    documentCount: number;
    vendorKey?: string;
    vendorName?: string;
    vendorHero?: string;
    supplierId?: number;
    source?: ProjectSource;
    allowedActions?: ProjectAction[];
}

export interface Project {
    id: number;
    organizationId: number;
    name: string;
    type: ProjectType;
    status: ProjectStatus;
    creator: User;
    creatorId: number;
    owner?: User;
    ownerId: number;
    recordCreationDate: Date;
    recordUpdateDate: Date;
    lastUpdateDate: Date;
    submittedDate?: Date;
    completedDate?: Date;
    recordDeletionDate?: Date;
    orderForm?: OrderForm;
    tactic?: Tactic;
    documents?: ProjectDocument[];
    analyses: ProjectAnalysis[];
    collaborators?: ProjectCollaborator[];
    allowedActions?: ProjectAction[];
    closingOutcome?: ClosingOutcome;
    source?: ProjectSource;
    /** Present on RFx projects. */
    rfx?: Rfx;
}

/** RFx-specific data hanging off a `Project` when `type === RFX`. */
export interface Rfx {
    id: number;
    vendorsSelected: boolean; // user-controlled "yes, vendor(s) were selected"
    vendors: RfxVendor[]; // one entry per evaluated vendor candidate
}

export function canDo(project: Project | ProjectSummary, action: ProjectAction): boolean {
    return project.allowedActions?.includes(action) ?? false;
}

/** Convert a full Project to a ProjectSummary for use in ProjectCard header of ProjectView. */
export function toProjectSummary(project: Project): ProjectSummary {
    return {
        id: project.id,
        name: project.name,
        type: project.type,
        status: project.status,
        closingOutcome: project.closingOutcome,
        creatorId: project.creatorId,
        creatorName: project.creator?.name || "Unknown",
        ownerId: project.ownerId,
        ownerName: project.owner?.name,
        recordCreationDate: project.recordCreationDate,
        lastUpdateDate: project.lastUpdateDate,
        latestAnalysis: project.status !== ProjectStatus.DRAFT
            ? (() => {
                const latest = getLatestAnalysis(project);
                return { type: latest.type, version: latest.version, status: latest.status };
            })()
            : undefined,
        documentCount: project.documents?.length ?? 0,
        vendorKey: project.orderForm?.vendorKey,
        vendorName: project.orderForm?.vendor?.name,
        vendorHero: project.orderForm?.vendor?.hero,
        supplierId: project.orderForm?.vendor?.supplier?.id,
        source: project.source,
        allowedActions: project.allowedActions,
    };
}

export function isDealProject(project: Project | ProjectSummary): boolean {
    return project.type === ProjectType.DEAL;
}

export function isRfxProject(project: Project | ProjectSummary): boolean {
    return project.type === ProjectType.RFX;
}

export function getProjectTypeLabel(projectType: ProjectType): string {
    switch (projectType) {
        case ProjectType.DEAL:
            return "deal";
        case ProjectType.RFX:
            return "RFX";
    }
}

/** The ProjectAnalysis for the given version number, otherwise undefined. */
export function getAnalysisByVersion(project: Project, version: number): ProjectAnalysis {
    // Assume that all projects have at least one analysis.
    // Which is true since we create an analysis when we create a project.
    return project.analyses.find((a) => a.version === version)!;
}

export function getLatestAnalysis(project: Project): ProjectAnalysis {
    const version = Math.max(...project.analyses.map((a) => a.version));
    return getAnalysisByVersion(project, version);
}

export interface ProjectCreateRequest {
    name?: string;
    type: ProjectType;
    survey?: {
        schema: object;
        response: object;
    };
    files?: File[];
    /** Origin of this project when spawned from another (e.g. an RFx). */
    source?: ProjectSourceInput;
    /** If true, submit the first analysis immediately after creation. */
    submitImmediately?: boolean;
}

export interface ProjectUpdateRequest {
    name?: string;
    type?: ProjectType;
    survey?: {
        schema: object;
        response: object;
    };
    ownerId?: number;
}

// Project Dashboard Data

export interface DashboardCount {
    projectCount: number;
    documentCount: number;
}

export interface Dashboard {
    count: {
        total: DashboardCount;
        byStatus: {
            // Keys are only present for statuses that have at least one project
            // (the dashboard query groups by status), so treat each as optional.
            [key in ProjectStatus]?: DashboardCount;
        };
    };
}

// Deal Analysis Output

export enum DynamicLeverEvaluation {
    NOT_FAVORABLE = "Not Favorable",
    NEUTRAL = "Neutral",
    FAVORABLE = "Favorable",
    NOT_PRESENT = "Not Present",
}

export interface DynamicLever {
    name?: string;
    type?: string;
    presentInContract?: boolean; // whether the lever is present in the contract (which can be a favorable thing or not)
    evaluation?: DynamicLeverEvaluation;
    currentState?: string;
    isActionable: boolean; // whether the lever is NOT in good standing, i.e. actionable
    priority?: number; // priority for levers not in good standing
    optimalState?: string; // optimal state for levers not in good standing
    justification?: string; // justification for levers not in good standing
}

export enum Evaluation {
    NOT_FAVORABLE = "Not Favorable",
    NEUTRAL = "Neutral",
    FAVORABLE = "Favorable",
}

export type FeedbackValue = "good" | "bad";

export interface AnalysisFeedback {
    userId: number;
    date: string; // ISO date string
    value: FeedbackValue;
    comment?: string;
}

export enum AnalysisOutputType {
    DEAL = "deal_output",
    RFX = "rfx_output",
    FLEXIBLE = "flexible_output",
}

// ── Structured Output

export type DealInfo = {
    // Exactly one of `vendorKey` or `vendorName` must be supplied.
    // `vendorKey` references an existing `il_unified_vendor` row directly.
    // `vendorName` is the raw vendor name from contract extraction; the server
    // resolves it to a vendor (creating one if no substring match exists).
    vendorKey?: string;
    vendorName?: string;
    value: number; // in USD cents
    orderStartDate?: string; // ISO date string
    orderEndDate?: string; // ISO date string
    autoRenewal: boolean;
    orderScore: number;
    optimizedScore: number;
    ownerId?: number; // defaults to project owner
    increase?: number; // in USD cents
    estimatedSavingsLow?: number; // estimated savings (low end) in USD cents
    estimatedSavingsHigh?: number; // estimated savings (high end) in USD cents
    realizedSavings?: number; // realized savings in USD cents
}

export enum StructuredOutputType {
    DEAL = "deal",
    RFX = "rfx",
}

export interface DealStructuredOutput {
    type: StructuredOutputType.DEAL;
    label: string;
    data: {
        dealInfo?: DealInfo,
        levers?: DynamicLever[];
    }
}

export interface RfxStructuredOutputData {
    vendors: Array<{
        vendorKey: string;
    }>;
}

export interface RfxStructuredOutput {
    type: StructuredOutputType.RFX;
    data: RfxStructuredOutputData;
}

export type StructuredOutput = DealStructuredOutput | RfxStructuredOutput;

// ── Flexible Output ──

export interface FlexibleAnalysisOutput {
    type: AnalysisOutputType.FLEXIBLE;
    content: string; // HTML for main content area (required)
    structuredOutputs?: StructuredOutput[];
}

// ── Legacy Output ──
 
export interface DealAnalysisOutput {
    type: AnalysisOutputType.DEAL;
    dealInfo?: Omit<DealInfo, "estimatedSavingsLow" | "estimatedSavingsHigh" | "realizedSavings">;
    levers?: DynamicLever[]; // Dynamic Levers
    summary?: {
        text: string; // human-readable description of what happened this round
        estimatedSavings?: [number, number]; // estimated savings in USD cents (version 1 only)
        realizedSavings?: number; // realized savings in USD cents (version 2+ only)
        strategicProgress: {
            prioritiesIdentified: number;
            prioritiesAchieved?: number; // v2+ only
        };
        evaluation: {
            overall: Evaluation;
            pricing?: Evaluation;
            structuralFlexibility?: Evaluation;
            operationalAndLegalProtections?: Evaluation;
            strategicFit?: Evaluation;
        };
        implications: Array<{ category: string; implication: string }>;
        nextActions: Array<{ action: string; justification: string }>;
    };
    report?: {
        contractSummary: string; // HTML
        topLegalPriorities: string; // HTML
        negotiationStrategy: string; // HTML
    };
    sourceUrls?: {
        summary?: string; // Google Sheet URL for summary import
        report?: string; // Google Doc URL for report import
        levers?: string; // Google Sheet URL for levers import
    };
}

export interface RfxAnalysisOutput {
    type: AnalysisOutputType.RFX;
    report?: {
        content: string; // HTML
    };
    sourceUrls?: {
        report?: string; // Google Doc URL for report import
    };
    vendors?: Array<{
        vendorKey: string;
    }>;
}

export type AnalysisOutput = DealAnalysisOutput | RfxAnalysisOutput | FlexibleAnalysisOutput;

export function isDealOutput(output: AnalysisOutput | undefined): output is DealAnalysisOutput {
    return output?.type === AnalysisOutputType.DEAL;
}

export function isRfxOutput(output: AnalysisOutput | undefined): output is RfxAnalysisOutput {
    return output?.type === AnalysisOutputType.RFX;
}

export function isFlexibleOutput(output: AnalysisOutput | undefined): output is FlexibleAnalysisOutput {
    return output?.type === AnalysisOutputType.FLEXIBLE;
}

// Project Analysis

export enum ProjectAnalysisStatus {
    DRAFT = "draft",
    SUBMITTED = "submitted",
    REQUIRE_USER_ACTION = "require_user_action",
    COMPLETED = "completed",
}

export interface SurveySchema {
    schema: object;
    version: string;
}

export interface SurveyData {
    schema: object;
    response: object;
}

export interface ProjectAnalysisUpdateRequest {
    survey: {
        schema: object;
        response: object;
    };
}

export type InputDocumentChange = "added" | "removed";

export interface InputDocument {
    id: number;
    document: Document;
    change?: InputDocumentChange;
}

export interface ProjectAnalysisInput {
    revision: number;
    survey?: {
        schema: object;
        response: object;
    };
    surveySchemaVersion?: string;
    documents: InputDocument[];
    creator: User;
    recordCreationDate: Date;
}

export interface ProjectAnalysis {
    id: number;
    type: AnalysisType;
    version: number;
    status: ProjectAnalysisStatus;
    requireUserActionMessage?: string | null;
    inputs?: ProjectAnalysisInput[];
    outputType?: AnalysisOutputType; // set when this analysis has a saved output; body is fetched lazily via getAnalysisOutput
    userFeedback?: Record<string, AnalysisFeedback[]>;
    creator: User;
    recordCreationDate: Date;
    recordUpdateDate: Date;
    submittedDate?: Date;
    completedDate?: Date;
    documents: Document[];
    attachments?: Document[];
}

/** Whether an analysis is a closing analysis (ROI report or complete). */
export const isClosingAnalysis = (analysis: ProjectAnalysis): boolean =>
    analysis.type === AnalysisType.ROI || analysis.type === AnalysisType.COMPLETE;

/** Latest analysis status for a project or project summary. */
export function getLatestAnalysisStatus(project: Project | ProjectSummary): ProjectAnalysisStatus | undefined {
    if ("analyses" in project) {
        return getLatestAnalysis(project).status;
    }
    return project.latestAnalysis?.status;
}

/** Status label and tag color for a project or project summary. */
export function getStatus(project: Project | ProjectSummary): { label: string; color: string } {
    if (project.status === ProjectStatus.DRAFT) return { label: "Draft", color: "default" };
    if (project.closingOutcome) {
        switch (project.closingOutcome) {
            case "signed": return { label: "Signed", color: "green" };
            case "cancelled": return { label: "Cancelled", color: "red" };
            case "completed": return { label: "Complete", color: "green" };
        }
    }
    switch (getLatestAnalysisStatus(project)) {
        case ProjectAnalysisStatus.DRAFT: return { label: "Draft", color: "default" };
        case ProjectAnalysisStatus.SUBMITTED: return { label: "Pending", color: "orange" };
        case ProjectAnalysisStatus.REQUIRE_USER_ACTION: return { label: "Changes requested", color: "red" };
        case ProjectAnalysisStatus.COMPLETED: return { label: "Ready", color: "green" };
        default: return { label: "–", color: "default" };
    }
}

/** Display label for an analysis. */
export function getAnalysisLabel(analysis: ProjectAnalysis): string {
    switch (analysis.type) {
        case AnalysisType.DEAL:
        case AnalysisType.RFX:
            return `Playbook v${analysis.version}`;
        case AnalysisType.ROI:
            return "ROI report";
        case AnalysisType.COMPLETE:
            return "Complete";
    }
}

export interface SaveAnalysisOutputRequest {
    draft: boolean; // if true, saves but do not share with user yet
    output: AnalysisOutput;
}

export interface SaveAnalysisFeedbackRequest {
    path: string; // e.g. "report.contractSummary.0"
    value: "good" | "bad";
    comment?: string;
}
